import type { CSSProperties } from "react";

import { REPORTING_COPY } from "./reporting.constants.ts";
import type { ComparisonBar, TimeBucket } from "./reporting.model.ts";

/**
 * The two graphs on Costs & Income — OND-97.
 *
 * NO LIBRARY, AND NO SVG EITHER. There is no charting dependency in this
 * repository and these are its first charts; two bar charts over four numbers
 * do not justify adding one, and a library would bring its own type scale and
 * palette into a design system that has square corners, nine type steps and a
 * named palette of its own. SVG was the obvious second choice and bought
 * nothing here: every mark is an axis-aligned rectangle whose length is a
 * proportion, which is what a block element with a width already is — and in
 * CSS the hatch, the minimum visible sliver and the responsive reflow are all
 * one declaration each instead of arithmetic. The one thing SVG would have
 * added is a `<text>` per label, which the surrounding HTML does better.
 *
 * EVERY FIGURE IS ALREADY DECIDED. The model hands over a `fraction` between 0
 * and 1 and a formatted `value`; these components set the fraction as a custom
 * property and print the string. There is no arithmetic about money here, no
 * max, no scale, and no branch about what a bar means — the view's rule kept
 * literally, because the alternative is a chart that can disagree with the
 * table beneath it.
 *
 * COLOUR IS NEVER THE ONLY CUE, which is what OND-97 asks for and the reason
 * for the hatch. Cost and income are two hues — `--series-cost` and
 * `--series-income`, chosen by running the pair through a CVD validator rather
 * than by eye, and passing protan, deutan and tritan separation against both
 * surfaces this app uses — while OUTSTANDING against RECORDED is a diagonal
 * hatch against a solid fill. So a reader who cannot tell the two hues apart
 * still sees which half of a column has moved, and a greyscale print carries
 * both distinctions. Neither series uses a status colour: an ONDO fee is not a
 * warning and resale income is not a success.
 *
 * AND THE TABLE BENEATH IS THE TABULAR EQUIVALENT. Each chart is one `img` to
 * assistive technology, named for what it plots, rather than a tree of hundreds
 * of elements read out one at a time — the figures themselves are in the table,
 * which is a better thing to read than a chart.
 */

/** A proportion the model computed, handed to the stylesheet. */
function fraction(value: number): CSSProperties {
  return { "--fraction": value } as CSSProperties;
}

/**
 * The at-a-glance comparison: four horizontal bars on one scale.
 *
 * HORIZONTAL, because the four labels are sentences ("Resale share recorded as
 * received") and vertical columns would either truncate them or turn them
 * sideways. The figure is printed at the end of every bar — there are four, so
 * this is the one chart here where a label on every mark is right rather than
 * clutter, and it is what makes a genuinely tiny bar readable when a fee tariff
 * of a few hundred is set against a six-figure settlement on one axis.
 *
 * ONE AXIS. Giving the fees a scale of their own would let a reader compare two
 * heights that are not comparable, which is the most misleading thing a chart
 * of this data could do.
 */
export function ComparisonChart({
  bars,
  currency,
  label,
}: {
  bars: ComparisonBar[];
  currency: string;
  label: string;
}) {
  return (
    <figure className="reporting__chart">
      <figcaption className="reporting__chart-title">
        {REPORTING_COPY.charts.comparisonTitle}
        <span className="reporting__chart-axis">
          {REPORTING_COPY.charts.axisLabel(currency)}
        </span>
      </figcaption>
      <p className="reporting__chart-help">{REPORTING_COPY.charts.comparisonHelp}</p>

      <div className="reporting__bars" role="img" aria-label={label}>
        {bars.map((bar) => (
          <div key={bar.key} className="reporting__bar-row">
            <span className="reporting__bar-label">{bar.label}</span>
            <span className="reporting__bar-track">
              <span
                className={`reporting__bar reporting__bar--${bar.series} reporting__bar--${bar.tone}`}
                style={fraction(bar.fraction)}
              />
            </span>
            <span className="reporting__bar-value">{bar.value}</span>
          </div>
        ))}
      </div>
    </figure>
  );
}

/**
 * The period over time: two stacked columns per bucket.
 *
 * TWO COLUMNS AND NOT FOUR SERIES SIDE BY SIDE. Twelve months of four bars is
 * forty-eight marks and reads as noise; two — cost and income — each split into
 * what has been recorded and what is still outstanding, reads as the question
 * the screen is asking. The stack is honest rather than decorative: every
 * record is in exactly one segment, so a column's height is what that bucket
 * raised and nothing is counted twice.
 *
 * RECORDED SITS AT THE BASELINE and outstanding above it, because the recorded
 * part is the settled half of the figure and the half a reader trusts first.
 *
 * AN EMPTY BUCKET KEEPS ITS SLOT. The model emits it; this prints its label and
 * a baseline, so the axis stays continuous and February never sits beside April.
 *
 * NO DIRECT LABELS, deliberately — a figure on every segment of twenty-four
 * would be unreadable. Each segment carries a `title`, which is both a native
 * tooltip and that shape's accessible name, and the table below carries every
 * figure in full.
 */
export function TimeChart({
  buckets,
  currency,
  label,
  grouping,
}: {
  buckets: TimeBucket[];
  currency: string;
  label: string;
  grouping: string;
}) {
  if (buckets.length === 0) {
    return (
      <figure className="reporting__chart">
        <figcaption className="reporting__chart-title">
          {REPORTING_COPY.charts.timeTitle}
        </figcaption>
        <p className="reporting__chart-help">{REPORTING_COPY.charts.noData}</p>
      </figure>
    );
  }

  return (
    <figure className="reporting__chart">
      <figcaption className="reporting__chart-title">
        {REPORTING_COPY.charts.timeTitle}
        <span className="reporting__chart-axis">
          {`${REPORTING_COPY.charts.axisLabel(currency)} · ${grouping}`}
        </span>
      </figcaption>

      <ul className="reporting__legend">
        {LEGEND.map((entry) => (
          <li key={entry.key} className="reporting__legend-item">
            <span
              className={`reporting__swatch reporting__swatch--${entry.series} reporting__swatch--${entry.tone}`}
              aria-hidden="true"
            />
            {entry.label}
          </li>
        ))}
      </ul>

      {/* The chart scrolls rather than squeezing: a quarter of daily buckets is
          wider than a phone, and columns narrower than a few pixels are not a
          chart. */}
      <div className="reporting__time-scroll">
        <div className="reporting__time" role="img" aria-label={label}>
          {buckets.map((bucket) => (
            <div
              key={bucket.key}
              className={`reporting__bucket${bucket.empty ? " reporting__bucket--empty" : ""}`}
            >
              <div className="reporting__columns">
                {bucket.columns.map((col) => (
                  <div
                    key={col.key}
                    className={`reporting__column reporting__column--${col.series}`}
                    title={`${bucket.label} · ${col.label} ${col.total}`}
                  >
                    {col.segments.map((segment) => (
                      <span
                        key={segment.key}
                        className={`reporting__segment reporting__segment--${col.series} reporting__segment--${segment.tone}`}
                        style={fraction(segment.fraction)}
                        title={`${bucket.label} · ${segment.label} ${segment.value}`}
                      />
                    ))}
                  </div>
                ))}
              </div>
              <span className="reporting__bucket-label">{bucket.label}</span>
            </div>
          ))}
        </div>
      </div>
    </figure>
  );
}

/**
 * The four legend entries, in the order the tiles and the comparison use them.
 *
 * Here rather than in the constants file because each entry pairs a label with
 * the two class-bearing values that draw its swatch, and that pairing is about
 * this component's markup rather than about the product's vocabulary. The
 * labels themselves come from the constants, so the legend and the tiles cannot
 * disagree about what a measure is called.
 */
const LEGEND = [
  {
    key: "fees-paid",
    series: "cost",
    tone: "recorded",
    label: REPORTING_COPY.charts.legend.feesPaid,
  },
  {
    key: "fees-due",
    series: "cost",
    tone: "due",
    label: REPORTING_COPY.charts.legend.feesDue,
  },
  {
    key: "shares-received",
    series: "income",
    tone: "recorded",
    label: REPORTING_COPY.charts.legend.sharesReceived,
  },
  {
    key: "shares-due",
    series: "income",
    tone: "due",
    label: REPORTING_COPY.charts.legend.sharesDue,
  },
] as const;
